import { NO, YES } from "uicore-ts"


// MARK: - RHAltModeRegistrant

/**
 * Implemented by RHAltModeKeyHandler — the per-button object that owns badge
 * drawing and key-press handling for a single registered shortcut letter.
 */
export interface RHAltModeRegistrant {
    
    readonly altKey: string
    
    /** Whether the button this registrant belongs to is currently in the view tree. */
    readonly isInViewTree: boolean
    
    enterAltMode(): void
    exitAltMode(): void
    
    /** Return YES if this registrant consumed the key. */
    handleAltKey(key: string): boolean
    
}


// MARK: - RHAltModeManager

export class RHAltModeManager {
    
    static readonly shared = new RHAltModeManager()
    
    _registrants: RHAltModeRegistrant[] = []
    _isActive = NO
    _savedFocusedElement?: Element
    
    
    /**
     * Register a key handler. Multiple registrants may share the same key
     * letter provided they belong to mutually exclusive views (e.g. a login
     * button and a logout button that are never both in the view tree at the
     * same time). At runtime, `enterAltMode` and `handleAltKey` on each
     * handler skip themselves when their display view is not in the view tree,
     * so only the visible one reacts.
     *
     * Calls `_warnIfKeyAmbiguous` after registration so that a conflict is
     * reported immediately if both buttons happen to be visible at startup.
     */
    registerRegistrant(registrant: RHAltModeRegistrant): boolean {
        if (!this._registrants.contains(registrant)) {
            this._registrants.push(registrant)
        }
        this._warnIfKeyAmbiguous(registrant.altKey)
        return YES
    }
    
    
    _warnIfKeyAmbiguous(key: string) {
        const upperKey = key.toUpperCase()
        const visibleConflicts = this._registrants.filter(
            registrant => registrant.altKey.toUpperCase() === upperKey && registrant.isInViewTree
        )
        if (visibleConflicts.length > 1) {
            console.warn(
                `[RHAltModeManager] Alt-key "${upperKey}" is in use by ${visibleConflicts.length} ` +
                `buttons that are all currently in the view tree — this key will be ambiguous. ` +
                `Check your button configuration for duplicate keys.`
            )
        }
    }
    
    
    deregisterRegistrant(registrant: RHAltModeRegistrant) {
        const index = this._registrants.indexOf(registrant)
        if (index > -1) {
            this._registrants.splice(index, 1)
        }
    }
    
    
    get isActive(): boolean {
        return this._isActive
    }
    
    
    // MARK: - Window focus loss
    
    /**
     * Exits alt-mode whenever the window loses focus or the document becomes
     * hidden. On Windows, Alt+Tab sends an Alt keydown in this window but the
     * matching keyup fires in the newly focused window, so alt-mode would
     * otherwise stay active indefinitely after switching away.
     *
     * Called once by the module initialiser at the bottom of this file.
     */
    _installFocusLossListeners() {
        window.addEventListener("blur", () => { this.exitAltMode() })
        document.addEventListener("visibilitychange", () => {
            if (document.hidden) {
                this.exitAltMode()
            }
        })
    }
    
    
    enterAltMode() {
        if (this._isActive) {
            return
        }
        this._isActive = YES
        this._savedFocusedElement = document.activeElement ?? undefined
        for (const registrant of this._registrants) {
            registrant.enterAltMode()
        }
    }
    
    
    exitAltMode() {
        if (!this._isActive) {
            return
        }
        this._isActive = NO
        for (const registrant of this._registrants) {
            registrant.exitAltMode()
        }
        if (this._savedFocusedElement && this._savedFocusedElement instanceof HTMLElement) {
            this._savedFocusedElement.focus()
        }
        this._savedFocusedElement = undefined
    }
    
    
    handleKey(key: string): boolean {
        if (!this._isActive) {
            return false
        }
        const upperKey = key.toUpperCase()
        this._warnIfKeyAmbiguous(upperKey)
        for (const registrant of this._registrants) {
            if (registrant.handleAltKey(upperKey)) {
                this.exitAltMode()
                return true
            }
        }
        return false
    }
    
}


// MARK: - Module initialisation

RHAltModeManager.shared._installFocusLossListeners()
